import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import {
  FaBold, FaItalic, FaUnderline, FaStrikethrough,
  FaSuperscript, FaSubscript, FaListUl, FaListOl,
  FaOutdent, FaIndent, FaAlignLeft, FaAlignCenter,
  FaAlignRight, FaAlignJustify, FaLink, FaUnlink,
  FaImage, FaTable, FaUndo, FaRedo, FaTrash, FaFilm, FaCode,
  FaPaintBrush, FaFont,
} from "react-icons/fa";
import "highlight.js/styles/atom-one-dark.css";
import "../../styles/RichTextEditor.css";
import {
  CODE_LANGUAGES, FONT_OPTIONS, IMAGE_WIDTHS, TEXT_COLORS, TABLE_PICKER,
  extractYouTubeId, uploadImage, setUploadBase, getTableSizes, isFormatPainterActive,
  FULL_EXTENSIONS, BASIC_EXTENSIONS,
} from "./richTextExtensions";

// The schema lives in richTextExtensions.js, shared with the Trainer Portal's
// note editor. Only the upload origin is app-specific.
setUploadBase(process.env.REACT_APP_API_URL || "");

// ── Reusable toolbar primitives ───────────────────────────────────────────────
const Btn = ({ onClick, active, title, children, danger }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`rte-btn${active ? " is-active" : ""}${danger ? " rte-btn-danger" : ""}`}
    title={title}
  >
    {children}
  </button>
);

const Sep = () => <span className="rte-sep" />;

// ── Toolbar popover ───────────────────────────────────────────────────────────
// A toolbar button that opens a small panel beneath it. Closes on a click
// outside, on Escape, or when the panel calls close(). Opens leftwards when it
// would run off the right edge of the window.
const Popover = ({ title, active, button, children }) => {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!anchorRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    const rect = panelRef.current?.getBoundingClientRect();
    setAlignRight(!!rect && rect.right > window.innerWidth - 8);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className="rte-pop-anchor" ref={anchorRef}>
      <Btn onClick={() => setOpen((v) => !v)} active={open || active} title={title}>{button}</Btn>
      {open && (
        <div ref={panelRef} className={`rte-pop${alignRight ? " rte-pop-right" : ""}`} role="dialog" aria-label={title}>
          {children(() => setOpen(false))}
        </div>
      )}
    </span>
  );
};

// ── Table size picker ─────────────────────────────────────────────────────────
// Hover the grid to choose up to 10 × 8, or type exact numbers for anything
// bigger. The header-row choice applies to both.
const TablePicker = ({ editor, close }) => {
  const [hover, setHover] = useState({ rows: 0, cols: 0 });
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [header, setHeader] = useState(true);

  const insert = (r, c) => {
    const clamp = (v, max) => Math.min(max, Math.max(1, parseInt(v, 10) || 1));
    editor.chain().focus().insertTable({
      rows: clamp(r, TABLE_PICKER.maxRows),
      cols: clamp(c, TABLE_PICKER.maxCols),
      withHeaderRow: header,
    }).run();
    close();
  };

  return (
    <div className="rte-table-pop">
      <div className="rte-pop-title">
        {hover.rows ? `${hover.rows} × ${hover.cols} table` : "Insert table"}
      </div>
      <div
        className="rte-table-grid"
        style={{ gridTemplateColumns: `repeat(${TABLE_PICKER.cols}, 18px)` }}
        onMouseLeave={() => setHover({ rows: 0, cols: 0 })}
      >
        {Array.from({ length: TABLE_PICKER.rows * TABLE_PICKER.cols }, (_, i) => {
          const r = Math.floor(i / TABLE_PICKER.cols) + 1;
          const c = (i % TABLE_PICKER.cols) + 1;
          const on = r <= hover.rows && c <= hover.cols;
          return (
            <button
              key={i}
              type="button"
              className={`rte-table-cell${on ? " is-on" : ""}`}
              aria-label={`${r} rows by ${c} columns`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHover({ rows: r, cols: c })}
              onFocus={() => setHover({ rows: r, cols: c })}
              onClick={() => insert(r, c)}
            />
          );
        })}
      </div>
      <div className="rte-pop-divider" />
      <div className="rte-pop-row">
        <label className="rte-pop-field">
          Rows
          <input type="number" min="1" max={TABLE_PICKER.maxRows} value={rows}
                 onChange={(e) => setRows(e.target.value)} />
        </label>
        <span className="rte-pop-times">×</span>
        <label className="rte-pop-field">
          Columns
          <input type="number" min="1" max={TABLE_PICKER.maxCols} value={cols}
                 onChange={(e) => setCols(e.target.value)} />
        </label>
      </div>
      <label className="rte-pop-check">
        <input type="checkbox" checked={header} onChange={(e) => setHeader(e.target.checked)} />
        First row is a header
      </label>
      <button type="button" className="rte-pop-primary" onClick={() => insert(rows, cols)}>
        Insert {Math.max(1, parseInt(rows, 10) || 1)} × {Math.max(1, parseInt(cols, 10) || 1)} table
      </button>
    </div>
  );
};

// ── Text colour picker ────────────────────────────────────────────────────────
const ColorPicker = ({ editor, current, close }) => {
  const [custom, setCustom] = useState(current || "#4f46e5");
  const apply = (color) => {
    if (color) editor.chain().focus().setColor(color).run();
    else editor.chain().focus().unsetColor().run();
    close();
  };
  return (
    <div className="rte-color-pop">
      <button type="button" className="rte-color-auto" onMouseDown={(e) => e.preventDefault()} onClick={() => apply(null)}>
        <span className="rte-swatch rte-swatch-auto" /> Automatic
      </button>
      <div className="rte-swatches">
        {TEXT_COLORS.map((c) => (
          <button
            key={c.value}
            type="button"
            className={`rte-swatch${current === c.value ? " is-current" : ""}`}
            style={{ background: c.value }}
            title={c.label}
            aria-label={c.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply(c.value)}
          />
        ))}
      </div>
      <div className="rte-pop-divider" />
      <div className="rte-pop-row">
        <label className="rte-pop-field rte-pop-field-inline">
          Custom
          <input type="color" value={custom} onChange={(e) => setCustom(e.target.value)} />
        </label>
        <button type="button" className="rte-pop-primary rte-pop-primary-sm" onClick={() => apply(custom)}>Apply</button>
      </div>
    </div>
  );
};

// ── Format painter button ─────────────────────────────────────────────────────
const FormatPainterBtn = ({ editor, active }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={() => (active
      ? editor.chain().cancelFormatPainter().run()
      : editor.chain().focus().copyFormat().run())}
    onDoubleClick={() => editor.chain().focus().copyFormat({ sticky: true }).run()}
    className={`rte-btn${active ? " is-active" : ""}`}
    title={active
      ? "Copying formatting — select text to apply it (Esc to stop)"
      : "Copy formatting: click, then select the text to apply it to. Double-click to apply to several."}
    aria-pressed={active}
  >
    <FaPaintBrush />
  </button>
);

// ── Code block control (toggle button + language picker) ──────────────────────
const CodeBlockControl = ({ editor }) => {
  const inCodeBlock = editor.isActive("codeBlock");
  const currentLang = editor.getAttributes("codeBlock").language || "plaintext";

  return (
    <>
      <Btn
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        active={inCodeBlock}
        title="Code block"
      >
        <FaCode />
      </Btn>
      {inCodeBlock && (
        <select
          className="rte-select"
          value={currentLang}
          onChange={(e) =>
            editor.chain().focus().updateAttributes("codeBlock", { language: e.target.value }).run()
          }
          title="Code language"
        >
          {CODE_LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      )}
    </>
  );
};

// ── Image format bar (shows when an image is selected) ────────────────────────
const ImageFormatBar = ({ editor }) => {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      active: e.isActive("image"),
      attrs: e.getAttributes("image"),
    }),
  });

  if (!state.active) return null;

  const { width: curWidth, align: curAlign } = state.attrs;

  const setWidth = (w) => editor.chain().focus().updateAttributes("image", { width: w }).run();
  const setAlign = (a) => editor.chain().focus().updateAttributes("image", { align: a }).run();
  const deleteImage = () => editor.chain().focus().deleteSelection().run();

  return (
    <div className="rte-format-bar">
      <span className="rte-format-label">Size:</span>
      {IMAGE_WIDTHS.map((w) => (
        <button
          key={w}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setWidth(w)}
          className={`rte-fmt-btn${curWidth === w ? " is-active" : ""}`}
          title={`Set width to ${w}`}
        >
          {w}
        </button>
      ))}
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setWidth(null)}
        className={`rte-fmt-btn${!curWidth ? " is-active" : ""}`}
        title="Original / auto width"
      >
        Auto
      </button>
      <Sep />
      <span className="rte-format-label">Align:</span>
      <Btn onClick={() => setAlign("none")} active={!curAlign || curAlign === "none"} title="No float">
        <FaAlignJustify />
      </Btn>
      <Btn onClick={() => setAlign("left")} active={curAlign === "left"} title="Float left">
        <FaAlignLeft />
      </Btn>
      <Btn onClick={() => setAlign("center")} active={curAlign === "center"} title="Centre">
        <FaAlignCenter />
      </Btn>
      <Btn onClick={() => setAlign("right")} active={curAlign === "right"} title="Float right">
        <FaAlignRight />
      </Btn>
      <Sep />
      <Btn onClick={deleteImage} danger title="Delete image">
        <FaTrash />
      </Btn>
    </div>
  );
};

// ── Table format bar (shows while the cursor is in a table) ──────────────────
// Typed sizes go with the drag handles on the table itself: column borders and
// the bottom edge of each row can both be dragged.
const SizeInput = ({ label, value, onCommit, title }) => {
  const [draft, setDraft] = useState(value ? String(value) : "");
  useEffect(() => { setDraft(value ? String(value) : ""); }, [value]);
  const commit = () => {
    const n = parseInt(draft, 10);
    const next = Number.isFinite(n) && n > 0 ? n : null;
    if (next !== (value || null)) onCommit(next);
  };
  return (
    <label className="rte-size-field" title={title}>
      {label}
      <input
        type="number"
        min="1"
        placeholder="auto"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
      />
      <span>px</span>
    </label>
  );
};

const Fmt = ({ onClick, children, title, disabled }) => (
  <button
    type="button"
    className="rte-fmt-btn"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    disabled={disabled}
  >
    {children}
  </button>
);

const TableFormatBar = ({ editor }) => {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e || !e.isActive("table")) return { active: false };
      return {
        active: true,
        canMerge: e.can().mergeCells(),
        canSplit: e.can().splitCell(),
        ...getTableSizes(e),
      };
    },
  });
  if (!state.active) return null;

  const run = (fn) => () => fn(editor.chain().focus()).run();

  return (
    <div className="rte-format-bar">
      <span className="rte-format-label">Table:</span>
      <Fmt onClick={run((c) => c.addRowBefore())} title="Insert a row above">+ Row above</Fmt>
      <Fmt onClick={run((c) => c.addRowAfter())} title="Insert a row below">+ Row below</Fmt>
      <Fmt onClick={run((c) => c.addColumnBefore())} title="Insert a column to the left">+ Col left</Fmt>
      <Fmt onClick={run((c) => c.addColumnAfter())} title="Insert a column to the right">+ Col right</Fmt>
      <Sep />
      <Fmt onClick={run((c) => c.deleteRow())} title="Delete this row">− Row</Fmt>
      <Fmt onClick={run((c) => c.deleteColumn())} title="Delete this column">− Col</Fmt>
      <Sep />
      <Fmt onClick={run((c) => c.mergeCells())} disabled={!state.canMerge} title="Merge the selected cells (drag across cells to select them)">Merge</Fmt>
      <Fmt onClick={run((c) => c.splitCell())} disabled={!state.canSplit} title="Split a merged cell">Split</Fmt>
      <Fmt onClick={run((c) => c.toggleHeaderRow())} title="Make the first row a header row, or a normal row">Header row</Fmt>
      <Sep />
      <SizeInput
        label="Row height"
        value={state.rowHeight}
        title="Height of the row the cursor is in. You can also drag the row's bottom edge."
        onCommit={(v) => editor.chain().focus().setRowHeight(v).run()}
      />
      <SizeInput
        label="Col width"
        value={state.colWidth}
        title="Width of the column the cursor is in. You can also drag the column's border."
        onCommit={(v) => editor.chain().focus().setColumnWidth(v).run()}
      />
      <Fmt onClick={run((c) => c.resetTableSize())} title="Clear all row heights and column widths — the table fills the width again">Reset size</Fmt>
      <Sep />
      <Btn onClick={run((c) => c.deleteTable())} danger title="Delete table"><FaTrash /></Btn>
    </div>
  );
};

// ── Main toolbars ─────────────────────────────────────────────────────────────
const FullToolbar = ({ editor }) => {
  // The editor does not re-render React on every transaction, so the toolbar
  // subscribes itself: whenever any state it shows changes, it re-renders and
  // the isActive() calls below read fresh values.
  const ui = useEditorState({
    editor,
    selector: ({ editor: e }) => (e ? {
      sig: ["bold", "italic", "underline", "strike", "superscript", "subscript", "bulletList",
            "orderedList", "codeBlock", "link", "table"].map((n) => (e.isActive(n) ? 1 : 0)).join("")
        + ["left", "center", "right", "justify"].map((a) => (e.isActive({ textAlign: a }) ? 1 : 0)).join("")
        + [1, 2, 3, 4].map((lv) => (e.isActive("heading", { level: lv }) ? 1 : 0)).join(""),
      font: e.getAttributes("textStyle").fontFamily || "",
      color: e.getAttributes("textStyle").color || null,
      painter: isFormatPainterActive(e),
    } : {}),
  }) || {};

  if (!editor) return null;

  const getHeadingLevel = () => {
    for (const lv of [1, 2, 3, 4]) {
      if (editor.isActive("heading", { level: lv })) return String(lv);
    }
    return "0";
  };

  const currentFont =
    FONT_OPTIONS.find((f) => f.value && editor.isActive("textStyle", { fontFamily: f.value }))?.value || "";

  const handleLink = () => {
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt("Enter URL:");
      if (url) editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      uploadImage(file)
        .then((src) => editor.chain().focus().setImage({ src }).run())
        .catch((err) => console.error("Image upload failed:", err));
    };
    input.click();
  };

  const handleVideo = () => {
    const url = window.prompt("Enter YouTube URL or direct video link (mp4, webm…):");
    if (!url?.trim()) return;
    const ytId = extractYouTubeId(url.trim());
    if (ytId) {
      editor.chain().focus().insertVideo({
        src: `https://www.youtube.com/embed/${ytId}`,
        kind: "youtube",
      }).run();
    } else {
      editor.chain().focus().insertVideo({ src: url.trim(), kind: "video" }).run();
    }
  };

  return (
    <div className="rte-toolbar">
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl+B)"><FaBold /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl+I)"><FaItalic /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline (Ctrl+U)"><FaUnderline /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><FaStrikethrough /></Btn>
      <Popover
        title="Text colour"
        active={!!ui.color}
        button={
          <span className="rte-color-btn">
            <FaFont />
            <span className="rte-color-bar" style={{ background: ui.color || "#0f172a" }} />
          </span>
        }
      >
        {(close) => <ColorPicker editor={editor} current={ui.color} close={close} />}
      </Popover>
      <FormatPainterBtn editor={editor} active={!!ui.painter} />
      <Sep />
      <Btn onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive("superscript")} title="Superscript"><FaSuperscript /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive("subscript")} title="Subscript"><FaSubscript /></Btn>
      <Sep />
      <select
        className="rte-select"
        value={getHeadingLevel()}
        onChange={(e) => {
          const lv = parseInt(e.target.value);
          if (lv === 0) editor.chain().focus().setParagraph().run();
          else editor.chain().focus().setHeading({ level: lv }).run();
        }}
        title="Paragraph style"
      >
        <option value="0">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
      </select>
      <select
        className="rte-select rte-select-font"
        value={currentFont}
        onChange={(e) => {
          const val = e.target.value;
          if (val) editor.chain().focus().setFontFamily(val).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
        title="Font family"
      >
        {FONT_OPTIONS.map((f) => (
          <option key={f.value} value={f.value}>{f.label}</option>
        ))}
      </select>
      <Sep />
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><FaListUl /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><FaListOl /></Btn>
      <Btn onClick={() => editor.chain().focus().liftListItem("listItem").run()} title="Decrease indent"><FaOutdent /></Btn>
      <Btn onClick={() => editor.chain().focus().sinkListItem("listItem").run()} title="Increase indent"><FaIndent /></Btn>
      <Sep />
      <CodeBlockControl editor={editor} />
      <Sep />
      <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left"><FaAlignLeft /></Btn>
      <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center"><FaAlignCenter /></Btn>
      <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right"><FaAlignRight /></Btn>
      <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify"><FaAlignJustify /></Btn>
      <Sep />
      <Btn onClick={handleLink} active={editor.isActive("link")} title={editor.isActive("link") ? "Remove link" : "Insert link"}>
        {editor.isActive("link") ? <FaUnlink /> : <FaLink />}
      </Btn>
      <Btn onClick={handleImage} title="Upload image"><FaImage /></Btn>
      <Btn onClick={handleVideo} title="Embed video (YouTube or direct URL)"><FaFilm /></Btn>
      <Popover title="Insert table" button={<FaTable />}>
        {(close) => <TablePicker editor={editor} close={close} />}
      </Popover>
      <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">—</Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo (Ctrl+Z)"><FaUndo /></Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo (Ctrl+Y)"><FaRedo /></Btn>
    </div>
  );
};

const BasicToolbar = ({ editor }) => {
  if (!editor) return null;

  const handleLink = () => {
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt("Enter URL:");
      if (url) editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="rte-toolbar">
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><FaBold /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><FaItalic /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><FaUnderline /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><FaStrikethrough /></Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><FaListUl /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><FaListOl /></Btn>
      <Sep />
      <CodeBlockControl editor={editor} />
      <Sep />
      <Btn onClick={handleLink} active={editor.isActive("link")} title={editor.isActive("link") ? "Remove link" : "Insert link"}>
        {editor.isActive("link") ? <FaUnlink /> : <FaLink />}
      </Btn>
      <Sep />
      <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo"><FaUndo /></Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo"><FaRedo /></Btn>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const RichTextEditor = forwardRef(function RichTextEditor(
  {
    value = "",
    onChange,
    onBlur,
    height = 400,
    toolbar = "full",
    placeholder = "Write here…",
  },
  ref,
) {
  const [isEmpty, setIsEmpty] = useState(!value || value === "<p></p>");

  const editor = useEditor({
    extensions: toolbar === "full" ? FULL_EXTENSIONS : BASIC_EXTENSIONS,
    content: value,
    editorProps: {
      attributes: {
        class: "rte-content",
        style: `min-height:${height}px`,
      },
    },
    onCreate: ({ editor }) => setIsEmpty(editor.isEmpty),
    onUpdate: ({ editor }) => {
      setIsEmpty(editor.isEmpty);
      onChange?.(editor.getHTML());
    },
    onBlur: ({ editor }) => onBlur?.(editor.getHTML()),
  });

  // Expose getHTML() so parent can read the latest content directly at save time
  useImperativeHandle(ref, () => ({
    getHTML: () => editor?.getHTML() ?? "",
  }), [editor]);

  // Sync value when parent loads data (e.g. edit form fetches from API)
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (editor.isFocused) return;
    const cur = editor.getHTML();
    const next = value || "";
    const curEmpty = !cur || cur === "<p></p>";
    const nextEmpty = !next || next === "<p></p>";
    if (curEmpty && nextEmpty) return;
    if (cur !== next) {
      editor.commands.setContent(next, false);
      setIsEmpty(editor.isEmpty);
    }
  }, [value, editor]);

  return (
    <div className="rte-wrap">
      {toolbar === "full" ? <FullToolbar editor={editor} /> : <BasicToolbar editor={editor} />}
      {toolbar === "full" && editor && <ImageFormatBar editor={editor} />}
      {toolbar === "full" && editor && <TableFormatBar editor={editor} />}
      <div className="rte-body">
        {isEmpty && placeholder && (
          <span className="rte-placeholder">{placeholder}</span>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
});

export default RichTextEditor;
